import { createApp, PropType } from "vue";
import vuetify from "./plugins/vuetify";
import router from "./router";
import type { RouteLocationNormalized, RouteLocationRaw } from "vue-router";
import { createPinia } from "pinia";
import { PiniaColada } from "@pinia/colada";
import * as Sentry from "@sentry/vue";
import { sentryApi } from "./api/sentry";
import { useAuthStore } from "./store/auth";
import { ensureAvatars, ensureUsers } from "./queries/users";
import VMarkdown from "@/components/VMarkdown.vue";
import { useSubscriptionsStore } from "./store/subscriptions";
import VueCookies from "vue-cookies";

declare global {
    function idProp<T extends string>(): PropType<T>;
    function objProp<T>(): T extends string ? false : PropType<Readonly<T>>;
    function arrProp<T>(): PropType<readonly T[]>;
}

globalThis.idProp = function idProp<T extends string>(): PropType<T> {
    return String as unknown as PropType<T>;
};

globalThis.objProp = function objProp<T>() {
    return Object as unknown as T extends string ? false : PropType<Readonly<T>>;
};

globalThis.arrProp = function arrProp<T>() {
    return Array as PropType<readonly T[]>;
};

void (async function () {
    const pinia = createPinia();

    // import late so the objProp stuff gets set before we start trying to load components
    const { default: App } = await import(/* webpackChunkName: "init" */ "./App.vue");

    // Colada's query cache captures pinia._a at creation, so the app must install the
    // plugins before anything touches the cache (ensureUsers below does).
    const app = createApp(App)
        .component("v-markdown", VMarkdown)
        .use(router)
        .use(pinia)
        .use(PiniaColada, { queryOptions: { staleTime: 30_000 } })
        .use(VueCookies, { expires: "90d" })
        .use(vuetify);

    const authStore = useAuthStore(pinia);
    const subscriptionsStore = useSubscriptionsStore(pinia);

    const authenticated = await authStore.authenticated();

    if (authenticated) {
        // pre-load users and avatars
        await ensureUsers(pinia);
        await ensureAvatars(pinia);
        await subscriptionsStore.load();
    }

    const sentryConfig = await sentryApi.get();
    Sentry.init({
        app,
        dsn: sentryConfig.dsn,
        tunnel: "/tunnel",
        release: sentryConfig.release,
        environment: sentryConfig.environment,
        integrations: [Sentry.browserTracingIntegration({ router }), Sentry.replayIntegration()],
        enabled: true,
        tracesSampleRate: 1.0,
        replaysSessionSampleRate: 1.0,
        replaysOnErrorSampleRate: 1.0,
        debug: sentryConfig.debug,
    });

    await router.isReady();

    const isGuestPage = router.currentRoute.value.matched.some((r) => r.meta["guest"]);
    // The portal subtree runs its own session against /api/portal/v0; the internal auth
    // guard must neither redirect into it nor out of it.
    const isPortalPage = router.currentRoute.value.matched.some((r) => r.meta["portal"]);

    // The portal store is imported on first use so its chunk stays out of the staff
    // entry; the session is scoped to the slug, so the guard needs the route params.
    async function portalRedirect(to: RouteLocationNormalized): Promise<RouteLocationRaw | null> {
        const slug = to.params["slug"];
        if (typeof slug !== "string" || slug === "") {
            return null;
        }

        const { usePortalStore } = await import("./portal/store/portal");
        const portalStore = usePortalStore(pinia);
        portalStore.setSlug(slug);

        if (!portalStore.checked) {
            await portalStore.authenticated();
        }

        // Public pages (the CSAT survey) work with or without a session: no redirect either way.
        if (to.matched.some((m) => m.meta["portalPublic"])) {
            return null;
        }

        const guestPage = to.matched.some((m) => m.meta["portalGuest"]);

        if (!portalStore.isAuthenticated && !guestPage) {
            portalStore.route = to.fullPath;
            return { name: "PortalLogin", params: { slug } };
        }

        if (portalStore.isAuthenticated && guestPage) {
            return { name: "PortalRequests", params: { slug } };
        }

        return null;
    }

    if (isPortalPage) {
        const redirect = await portalRedirect(router.currentRoute.value);
        if (redirect) {
            await router.replace(redirect);
        }
    } else if (!authenticated && !isGuestPage) {
        // store the route and redirect to login
        authStore.route = router.currentRoute.value.path;
        await router.push({ name: "AuthLogin" });
    } else if (authenticated && isGuestPage && router.currentRoute.value.path !== "/") {
        // redirect away from guest page
        await router.push("/");
    }

    router.beforeEach(async (to) => {
        if (to.matched.some((m) => m.meta["portal"])) {
            return (await portalRedirect(to)) ?? true;
        }

        const guest = to.matched.some((m) => m.meta["guest"]);

        if (!guest && !authStore.$state.isAuthenticated) {
            return { name: "AuthLogin" };
        }
        if (guest && authStore.$state.isAuthenticated) {
            return "/";
        }
        return true;
    });

    app.mount("#app");
})();
